// SPDX-License-Identifier: MIT
/**
 * logo-ignite (opening, 3–5 beats; reviewed in the Kestrel reel). A point of light appears, the
 * real mark draws itself as a hot filament while rings pulse on the beat, sparks shed from the
 * outline, the fill lands with a specular sweep, and the camera is pulled into it for the cut.
 * Without a logo the brand name is revealed by a travelling hot edge instead.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { fitLine, markBox, sceneBeats } from './lib.ts';
const CX = DESIGN_W / 2;
const CY = DESIGN_H / 2 - 10;
export default defineScene({
    id: 'logo-ignite',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        /** Optional small line under the mark once it lands (e.g. a category). */
        kicker: z.optional(z.string().check(z.maxLength(28))),
    }),
    glyphs: (p) => p.kicker ?? '',
    motion: (_p, env) => (env.localBeat > sceneBeats(env) - 1 ? 1 : 0.5),
    audioEvents: () => [{ beat: 0.4, sfx: 'blip' as const, gain: 0.6 }],
    post: (_p, env) => {
        const L = sceneBeats(env);
        const s = L / 4;
        return {
            bloom: env.style.background === 'dark' ? 1.25 + 0.8 * kit.beat.seg(env, 2.6 * s, 3.2 * s) : 0.6,
            exposure: 1 + 0.9 * kit.beat.seg(env, L - 0.7, L, kit.curves.launch),
            aberration: 0.6 * kit.beat.seg(env, L - 0.6, L),
        };
    },
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const L = sceneBeats(env);
        const s = L / 4;
        const logo = env.brand.logo;
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        kit.fx.pool(ctx, CX, CY, 900, th.primary, (th.dark ? 0.05 : 0.03) + 0.07 * kit.beat.seg(env, 1 * s, 3.5 * s));
        const push = kit.beat.seg(env, L - 0.9, L, kit.curves.launch);
        kit.camera.zoom(ctx, 1 + 0.06 * kit.beat.seg(env, 0, L - 0.9, kit.curves.silk) + 1.6 * push, CX, CY);
        // The ember: the origin of everything.
        const ember = kit.beat.seg(env, 0, 0.45, kit.curves.glide) * (1 - kit.beat.seg(env, 1.2 * s, 2.2 * s));
        if (ember > 0) {
            kit.fx.pool(ctx, CX, CY, 160, th.accent, 0.9 * ember);
            ctx.fillStyle = rgba(th.dark ? '#FFFFFF' : th.primary, ember);
            ctx.fillRect(CX - 3, CY - 3, 6, 6);
        }
        // Rings on the beats.
        ctx.lineWidth = 3;
        for (let b = 1; b < L; b++) {
            const k = kit.beat.seg(env, b, b + 1.2, kit.curves.glide);
            if (k <= 0 || k >= 1)
                continue;
            ctx.strokeStyle = rgba(th.primary, 0.85 * (1 - k) ** 1.5);
            ctx.beginPath();
            ctx.arc(CX, CY, 170 + 620 * k, 0, Math.PI * 2);
            ctx.stroke();
        }
        const drawOn = kit.beat.seg(env, 0.45 * s, 2.1 * s, kit.curves.glide);
        const fill = kit.beat.seg(env, 2.55 * s, 3.0 * s, kit.curves.glide);
        if (logo) {
            const box = markBox(env.brand, CX, CY, 1100, 300);
            const mark = kit.path.fit(logo.subpaths, box);
            env.report?.element('logo', [box.x, box.y, box.w, box.h]);
            // Sparks shed from the outline while it burns.
            const r = env.rng('sparks');
            ctx.save();
            ctx.globalCompositeOperation = th.light;
            kit.path.scatterAlong(mark, 260).forEach(([ox, oy], i) => {
                const birth = (0.9 + r() * 2.8) * s;
                const life = 0.6 + r() * 0.8;
                const dir = Math.atan2(oy - CY, ox - CX) + (r() - 0.5) * 1.1;
                const speed = 220 + r() * 640;
                const size = 3 + r() * 4;
                const age = (env.localBeat - birth) * env.spb;
                if (age <= 0 || age >= life)
                    return;
                const travel = (speed * (1 - Math.exp(-age * 2.8))) / 2.8;
                const x = ox + Math.cos(dir) * travel;
                const y = oy + Math.sin(dir) * travel + 140 * age * age;
                ctx.fillStyle = rgba(i % 4 === 0 ? th.hot : i % 2 ? th.primary : th.accent, (1 - age / life) ** 1.2);
                ctx.fillRect(x - size / 2, y - size / 2, size, size);
            });
            ctx.restore();
            // Filament: a primary stroke with a hot core, glowing.
            const line = Math.max(2, Math.min(7, box.h / 40));
            kit.fx.glow(ctx, (c) => {
                c.lineJoin = 'miter';
                c.lineWidth = line;
                c.strokeStyle = th.primary;
                kit.path.strokeProgress(c, mark, drawOn, 'parallel');
                if (th.dark) {
                    c.lineWidth = line * 0.3;
                    c.strokeStyle = th.hot;
                    kit.path.strokeProgress(c, mark, drawOn, 'parallel');
                }
            }, { color: th.primary, blur: th.dark ? 34 : 10 });
            if (fill > 0) {
                ctx.save();
                ctx.globalAlpha = fill;
                for (const part of logo.parts) {
                    ctx.fillStyle = part.fill ?? th.primary;
                    kit.path.tracePath(ctx, kit.path.fit(part.subpaths, box, logo.bounds));
                    ctx.fill(part.fillRule);
                }
                ctx.restore();
                kit.fx.sweep(ctx, (c) => {
                    c.fillStyle = '#FFFFFF';
                    kit.path.tracePath(c, mark);
                    c.fill(logo.fillRule);
                }, box, kit.beat.seg(env, 2.7 * s, 3.6 * s, kit.curves.silk), { width: Math.max(60, box.h * 0.3), intensity: th.dark ? 0.75 : 0.45 });
            }
        }
        else {
            // No logo: the name is revealed left to right behind a hot travelling edge.
            const name = env.brand.name;
            const px = fitLine(ctx, env.brand, name, 800, 200, 1400);
            ctx.font = font(env.brand, 800, px);
            ctx.letterSpacing = `${-px / 50}px`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const w = ctx.measureText(name).width;
            const left = CX - w / 2;
            const edge = left - 20 + (w + 40) * drawOn;
            ctx.save();
            ctx.beginPath();
            ctx.rect(left - 40, CY - px, edge - left + 40, px * 2);
            ctx.clip();
            ctx.fillStyle = th.fg;
            ctx.fillText(name, CX, CY);
            ctx.restore();
            if (drawOn > 0 && drawOn < 1) {
                kit.fx.pool(ctx, edge, CY, px * 0.9, th.primary, 0.8);
                ctx.fillStyle = th.hot;
                ctx.fillRect(edge - 2, CY - px * 0.55, 4, px * 1.1);
            }
            ctx.letterSpacing = '0px';
            env.report?.element('logo', [left, CY - px / 2, w, px]);
        }
        // Kicker under the mark once it lands.
        if (props.kicker) {
            const k = kit.beat.seg(env, 1.4 * s, 1.9 * s, kit.curves.glide) * (1 - push);
            if (k > 0) {
                ctx.textAlign = 'center';
                ctx.textBaseline = 'alphabetic';
                ctx.font = font(env.brand, 500, 28, 'mono');
                ctx.letterSpacing = '6px';
                ctx.fillStyle = rgba(th.fg, 0.7 * k);
                ctx.fillText(props.kicker, CX, CY + 240 + 20 * (1 - k));
                ctx.letterSpacing = '0px';
            }
        }
        // Speed lines as the camera is pulled in.
        if (push > 0) {
            const rl = env.rng('streaks');
            ctx.save();
            ctx.globalCompositeOperation = th.light;
            for (let i = 0; i < 70; i++) {
                const a = rl() * Math.PI * 2;
                const r0 = 200 + rl() * 500;
                const len = (80 + rl() * 260) * push;
                ctx.strokeStyle = rgba(th.accent, 0.35 * push);
                ctx.lineWidth = 1 + rl() * 1.5;
                ctx.beginPath();
                ctx.moveTo(CX + Math.cos(a) * r0, CY + Math.sin(a) * r0);
                ctx.lineTo(CX + Math.cos(a) * (r0 + len), CY + Math.sin(a) * (r0 + len));
                ctx.stroke();
            }
            ctx.restore();
        }
    },
});
